import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type {
  FilterWrapperParams,
  IAfterGuiAttachedParams,
} from 'ag-grid-community';
import type { CustomFilterDisplayProps } from 'ag-grid-react';
import { useGridFilterDisplay } from 'ag-grid-react';
import { CheckboxField, SearchField } from '@wk/components-v3-react16';
import { useGetWorkers } from './useGetWorkers';
import { useWorkerFilter } from './useWorkerFilter';
import { useWorkerSearch } from './useWorkerSearch';
import type { WorkerFilterModel } from './types';
import styles from './WorkerFilter.module.scss';

interface WorkerFilterProperties
  extends CustomFilterDisplayProps<WorkerFilterModel>,
    FilterWrapperParams {}

const WorkerFilter = ({
  model,
  onStateChange,
  state,
}: WorkerFilterProperties) => {
  const searchInputReference = useRef<HTMLInputElement>(null);
  const selectAllInputReference = useRef<HTMLInputElement>(null);
  const { data: workers, isLoading } = useGetWorkers();

  const afterGuiAttached = useCallback(
    (parameters?: IAfterGuiAttachedParams) => {
      if (!parameters?.suppressFocus) {
        searchInputReference.current?.focus();
      }
    },
    []
  );

  useGridFilterDisplay({ afterGuiAttached });
  useWorkerFilter(model);

  const { filteredWorkers, searchTerm, setSearchTerm } = useWorkerSearch(workers);

  const selectedWorkers: string[] = useMemo(
    () => state.model?.workers || [],
    [state.model?.workers]
  );

  const isAllFilteredSelected = useMemo(
    () =>
      filteredWorkers.length > 0 &&
      filteredWorkers.every((worker) => selectedWorkers.includes(worker)),
    [filteredWorkers, selectedWorkers]
  );

  const isIndeterminate = useMemo(() => {
    if (filteredWorkers.length === 0) {
      return false;
    }
    const selectedFilteredCount = filteredWorkers.filter((worker) =>
      selectedWorkers.includes(worker)
    ).length;
    return selectedFilteredCount > 0 && !isAllFilteredSelected;
  }, [filteredWorkers, selectedWorkers, isAllFilteredSelected]);

  const selectAllAriaChecked = useMemo<boolean | 'mixed'>(() => {
    if (isIndeterminate) {
      return 'mixed';
    }
    return isAllFilteredSelected;
  }, [isIndeterminate, isAllFilteredSelected]);

  useEffect(() => {
    if (selectAllInputReference.current) {
      selectAllInputReference.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const updateModel = useCallback(
    (updatedWorkers: string[]) => {
      const newModel = updatedWorkers.length > 0 ? { workers: updatedWorkers } : null;
      onStateChange({ model: newModel });
    },
    [onStateChange]
  );

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const worker = event.target.value;
      const isSelected = selectedWorkers.includes(worker);
      const updatedWorkers = isSelected
        ? selectedWorkers.filter((w) => w !== worker)
        : [...selectedWorkers, worker];
      updateModel(updatedWorkers);
    },
    [selectedWorkers, updateModel]
  );

  const handleSelectAll = useCallback(() => {
    if (isAllFilteredSelected) {
      const filteredSet = new Set(filteredWorkers);
      const remainingWorkers = selectedWorkers.filter((w) => !filteredSet.has(w));
      updateModel(remainingWorkers);
    } else {
      const allWorkers = Array.from(new Set([...selectedWorkers, ...filteredWorkers])); // eslint-disable-line unicorn/prefer-spread
      updateModel(allWorkers);
    }
  }, [isAllFilteredSelected, filteredWorkers, selectedWorkers, updateModel]);

  const handleKeydown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const spaceKeys = [' ', 'Space'];
      if (
        spaceKeys.includes(event.key) &&
        event.currentTarget.getAttribute('aria-disabled')
      ) {
        event.preventDefault();
      }
    },
    []
  );

  if (isLoading) {
    return <div className={styles.container}>Loading...</div>;
  }

  return (
    <div className={styles.container} tabIndex={-1}>
      <div className={styles.searchWrapper}>
        <SearchField
          searchButtonId="worker-search-button"
          size="large"
          hasClearIcon={searchTerm.length > 0}
          onClear={() => setSearchTerm('')}
        >
          <input
            ref={searchInputReference}
            type="search"
            name="worker-search"
            aria-labelledby="worker-search-button"
            autoComplete="off"
            onInput={(event) =>
              setSearchTerm((event.target as HTMLInputElement).value)
            }
            placeholder="Search workers..."
            value={searchTerm}
          />
        </SearchField>
      </div>

      {filteredWorkers.length > 0 && (
        <div className={styles.selectAllWrapper}>
          <CheckboxField
            labelFor="select-all-workers"
            label="Select All"
            size="extra-small"
          >
            <input
              ref={selectAllInputReference}
              id="select-all-workers"
              name="SelectAll"
              value="select-all"
              type="checkbox"
              checked={isAllFilteredSelected}
              aria-checked={selectAllAriaChecked}
              onChange={handleSelectAll}
              onKeyDown={handleKeydown}
              flow-id="checkbox"
            />
          </CheckboxField>
        </div>
      )}

      <div className={styles.checkboxList}>
        {filteredWorkers.length > 0 ? (
          filteredWorkers.map((worker) => {
            const isChecked = selectedWorkers.includes(worker);
            const inputId = `worker-${worker}`;
            return (
              <div key={worker} className={styles.checkboxWrapper}>
                <CheckboxField
                  labelFor={inputId}
                  label={worker}
                  size="extra-small"
                >
                  <input
                    id={inputId}
                    name={worker}
                    value={worker}
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    onKeyDown={handleKeydown}
                    flow-id="checkbox"
                  />
                </CheckboxField>
              </div>
            );
          })
        ) : (
          <div className={styles.noResults}>No matches</div>
        )}
      </div>
    </div>
  );
};

export default WorkerFilter;
